import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import {
  ClockIcon,
  UserPlusIcon,
  UserMinusIcon,
  PlusIcon,
  MinusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import GraphqlService from '../../service/graphqlService';
import RoleBadge from './RoleBadge';

const PAGE_SIZE = 8;

// When a filter is active we fetch a larger batch and filter/paginate locally,
// because the backend only supports exact-match filters (not free-text search).
const FILTER_FETCH_LIMIT = 200;

/**
 * Visual + textual metadata for each audit `change` type rendered in the feed.
 * Colors mirror the redesigned User Management screen.
 */
const CHANGE_META = {
  UserCreated: {
    Icon: UserPlusIcon,
    verb: 'created user',
    label: 'User created',
    preposition: null,
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    ring: 'ring-emerald-50',
    gradient: 'from-emerald-500 to-green-500',
    card: 'bg-emerald-50/40',
    pill: 'bg-emerald-100 text-emerald-700',
  },
  UserDeleted: {
    Icon: UserMinusIcon,
    verb: 'deleted user',
    label: 'User deleted',
    preposition: null,
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    ring: 'ring-rose-50',
    gradient: 'from-rose-500 to-red-500',
    card: 'bg-rose-50/40',
    pill: 'bg-rose-100 text-rose-700',
  },
  RoleAdded: {
    Icon: PlusIcon,
    verb: 'added role',
    label: 'Role added',
    preposition: 'to',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    ring: 'ring-blue-50',
    gradient: 'from-blue-500 to-indigo-500',
    card: 'bg-blue-50/40',
    pill: 'bg-blue-100 text-blue-700',
  },
  RoleRemoved: {
    Icon: MinusIcon,
    verb: 'removed role',
    label: 'Role removed',
    preposition: 'from',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    ring: 'ring-amber-50',
    gradient: 'from-amber-500 to-orange-500',
    card: 'bg-amber-50/40',
    pill: 'bg-amber-100 text-amber-700',
  },
};

const FALLBACK_META = {
  Icon: ClockIcon,
  verb: 'changed',
  label: 'Changed',
  preposition: null,
  iconBg: 'bg-gray-100',
  iconText: 'text-gray-500',
  ring: 'ring-gray-50',
  gradient: 'from-gray-400 to-gray-500',
  card: 'bg-gray-50/40',
  pill: 'bg-gray-100 text-gray-600',
};

/**
 * Extract the `project:role` group strings that should be rendered as badges
 * for a given audit row, based on its change type and `value` payload.
 *
 * @param {string} change - The audit change type.
 * @param {string} value - The audit `value` field.
 * @returns {string[]} A list of group strings (each containing a `:`).
 */
function getBadgeGroups(change, value) {
  if (!value || (change !== 'RoleAdded' && change !== 'RoleRemoved' && change !== 'UserDeleted')) {
    return [];
  }
  // UserDeleted may carry a comma-separated list of the deleted user's groups,
  // or simply a userId (no colon). RoleAdded/RoleRemoved carry a single group.
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.includes(':'));
}

/**
 * Friendly relative time (e.g. "2 hours ago") using moment, already a frontend dependency.
 * @param {string} timestamp - AWSDateTime ISO string.
 * @returns {string}
 */
function timeAgo(timestamp) {
  if (!timestamp) return '';
  const m = moment(timestamp);
  return m.isValid() ? m.fromNow() : '';
}

/**
 * A single timeline/feed row describing one user-management change.
 */
function AuditRow({ entry, isLast }) {
  const meta = CHANGE_META[entry.change] || FALLBACK_META;
  const { Icon } = meta;
  const actor = entry.user_name || entry.user_id || 'Someone';
  const target = entry.entity_id || 'a user';
  const badges = getBadgeGroups(entry.change, entry.value);
  const absoluteTime = entry.action_timestamp
    ? moment(entry.action_timestamp).format('MMM D, YYYY · HH:mm')
    : '';

  return (
    <li className="relative flex gap-4 pb-5">
      {/* Vertical connector line */}
      {!isLast && (
        <span
          className="absolute left-[19px] top-11 -bottom-1 w-px bg-gradient-to-b from-gray-200 to-transparent"
          aria-hidden="true"
        />
      )}

      {/* Colored circular icon with gradient ring */}
      <span
        className={`relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${meta.gradient} shadow-md ring-4 ring-white`}
      >
        <Icon className="h-5 w-5 text-white" />
      </span>

      <div
        className={`min-w-0 flex-1 rounded-xl border border-gray-100 ${meta.card} px-3.5 py-2.5 transition-colors hover:border-gray-200`}
      >
        <div className="mb-1">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.pill}`}
          >
            {meta.label}
          </span>
        </div>
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">{actor}</span>{' '}
          {meta.verb}
          {badges.length > 0 && meta.preposition ? ' ' : ' '}
          {/* Inline role badge(s) for role changes */}
          {(entry.change === 'RoleAdded' || entry.change === 'RoleRemoved') && badges.length > 0 && (
            <span className="mx-1 inline-flex align-middle">
              <RoleBadge group={badges[0]} size="sm" />
            </span>
          )}
          {meta.preposition && (
            <>
              {meta.preposition}{' '}
              <span className="font-semibold text-gray-900">{target}</span>
            </>
          )}
          {!meta.preposition && (
            <span className="font-semibold text-gray-900">{target}</span>
          )}
        </p>

        {/* Badges for a deleted user's former groups */}
        {entry.change === 'UserDeleted' && badges.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {badges.map((group) => (
              <RoleBadge key={group} group={group} size="sm" />
            ))}
          </div>
        )}

        <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-400" title={absoluteTime}>
          <ClockIcon className="h-3 w-3 text-gray-300" />
          {timeAgo(entry.action_timestamp)}
          {absoluteTime && <span className="ml-0.5 text-gray-300">· {absoluteTime}</span>}
        </p>
      </div>
    </li>
  );
}

/**
 * "Recent Activity" audit feed for the User Management screen.
 *
 * Presentational + self-contained data fetching: queries the backend audit log
 * for `PortalCognitoUsers` changes. The backend already scopes the rows by the
 * caller's access (superadmin → own projects, rootAccess → everything), so this
 * component does no scoping of its own.
 *
 * Refetch this panel after a mutation via
 * `queryClient.invalidateQueries(['userManagementAudit'])`.
 */
export default function AuditActivityPanel() {
  const [page, setPage] = useState(1);
  // Free-text filters: `actorFilter` matches who performed the action,
  // `targetFilter` matches the user the action was performed on.
  const [actorFilter, setActorFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [localPage, setLocalPage] = useState(1);

  const isFiltering = actorFilter.trim() !== '' || targetFilter.trim() !== '';

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['userManagementAudit', isFiltering ? 'filtered' : page],
    queryFn: async () => {
      const res = await GraphqlService.getAuditLogs({
        limit: isFiltering ? FILTER_FETCH_LIMIT : PAGE_SIZE,
        page: isFiltering ? 1 : page,
        entity: 'PortalCognitoUsers',
      });
      // The service may be unavailable/auto-mocked → guard against missing data.
      return res || null;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const entries = Array.isArray(data?.data) ? data.data : [];

  // Apply client-side filtering when a filter is active.
  const filteredEntries = useMemo(() => {
    if (!isFiltering) return entries;
    const actorTerm = actorFilter.trim().toLowerCase();
    const targetTerm = targetFilter.trim().toLowerCase();
    return entries.filter((entry) => {
      const actor = `${entry.user_name || ''} ${entry.user_id || ''}`.toLowerCase();
      const target = `${entry.entity_id || ''}`.toLowerCase();
      const actorMatch = !actorTerm || actor.includes(actorTerm);
      const targetMatch = !targetTerm || target.includes(targetTerm);
      return actorMatch && targetMatch;
    });
  }, [entries, isFiltering, actorFilter, targetFilter]);

  // Pagination: server-side when not filtering, local slicing when filtering.
  const pagination = data?.pagination;
  const serverPageNumber = pagination?.pageNumber ?? page;
  const serverTotalPages = pagination?.totalPages ?? 1;
  const serverIsLast = pagination?.isLast ?? true;

  const localTotalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
  const safeLocalPage = Math.min(localPage, localTotalPages);
  const visibleEntries = isFiltering
    ? filteredEntries.slice((safeLocalPage - 1) * PAGE_SIZE, safeLocalPage * PAGE_SIZE)
    : filteredEntries;

  const pageNumber = isFiltering ? safeLocalPage : serverPageNumber;
  const totalPages = isFiltering ? localTotalPages : serverTotalPages;
  const isLast = isFiltering ? safeLocalPage >= localTotalPages : serverIsLast;

  const handleActorFilterChange = (value) => {
    setActorFilter(value);
    setLocalPage(1);
  };

  const handleTargetFilterChange = (value) => {
    setTargetFilter(value);
    setLocalPage(1);
  };

  const clearFilters = () => {
    setActorFilter('');
    setTargetFilter('');
    setLocalPage(1);
  };

  const goToPreviousPage = () => {
    if (isFiltering) {
      setLocalPage((p) => Math.max(1, p - 1));
    } else {
      setPage((p) => Math.max(1, p - 1));
    }
  };

  const goToNextPage = () => {
    if (isFiltering) {
      setLocalPage((p) => p + 1);
    } else {
      setPage((p) => p + 1);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg ring-1 ring-gray-100/50">
      {/* Header */}
      <div className="flex items-start gap-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-violet-50 to-sky-50 px-6 py-5">
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-md">
          <ClockIcon className="h-6 w-6 text-white" />
        </span>
        <div>
          <h3 className="text-base font-bold text-gray-900">Recent Activity</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            The latest user-management changes — who did what, to whom, and when.
            Admins see only activity for the projects they manage.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-100 bg-gray-50/60 px-6 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="auditActorFilter" className="mb-1 block text-xs font-semibold text-gray-600">
              Performed by
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </span>
              <input
                id="auditActorFilter"
                type="text"
                value={actorFilter}
                onChange={(e) => handleActorFilterChange(e.target.value)}
                placeholder="Filter by who did it…"
                className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex-1">
            <label htmlFor="auditTargetFilter" className="mb-1 block text-xs font-semibold text-gray-600">
              Done to
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </span>
              <input
                id="auditTargetFilter"
                type="text"
                value={targetFilter}
                onChange={(e) => handleTargetFilterChange(e.target.value)}
                placeholder="Filter by who it was done to…"
                className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 sm:self-end"
            >
              <XMarkIcon className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-5">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            Loading recent activity…
          </div>
        )}

        {/* Error */}
        {!isLoading && isError && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <InboxIcon className="h-6 w-6 text-gray-400" />
            </span>
            <p className="mt-3 text-sm font-medium text-gray-600">Activity is unavailable right now</p>
            <p className="mt-1 max-w-sm text-xs text-gray-400">
              {error?.message || 'Could not load recent user-management changes.'}
            </p>
          </div>
        )}

        {/* Empty - no entries at all */}
        {!isLoading && !isError && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
              <ClockIcon className="h-6 w-6 text-indigo-400" />
            </span>
            <p className="mt-3 text-sm font-medium text-gray-700">No recent activity yet</p>
            <p className="mt-1 max-w-sm text-xs text-gray-400">
              User creations, deletions, and role changes will show up here as they happen.
            </p>
          </div>
        )}

        {/* Empty - filtered out */}
        {!isLoading && !isError && entries.length > 0 && visibleEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
            </span>
            <p className="mt-3 text-sm font-medium text-gray-700">No activity matches your filters</p>
            <p className="mt-1 max-w-sm text-xs text-gray-400">
              Try a different name, or clear the filters to see all recent changes.
            </p>
          </div>
        )}

        {/* Feed */}
        {!isLoading && !isError && visibleEntries.length > 0 && (
          <ul className="-mb-6">
            {visibleEntries.map((entry, idx) => (
              <AuditRow
                key={entry.id ?? `${entry.entity_id}-${entry.action_timestamp}-${idx}`}
                entry={entry}
                isLast={idx === visibleEntries.length - 1}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !isError && visibleEntries.length > 0 && (pageNumber > 1 || !isLast) && (
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3">
          <button
            type="button"
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:invisible"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>
          <span className="text-xs font-medium text-gray-400">
            Page {pageNumber} of {totalPages}
          </span>
          <button
            type="button"
            onClick={goToNextPage}
            disabled={isLast}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:invisible"
          >
            Next
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </section>
  );
}
